import React, { useState, useEffect } from 'react';
import { Grid } from 'lucide-react';

interface PuzzleSlideGameProps {
  onComplete: (score: number) => void;
}

function generateSolvedState(rows: number, cols: number) {
    const length = rows * cols;
    return [...Array(length - 1).keys()].map(i => i + 1).concat([0]); // [1, 2, ..., N-1, 0]
}

function getNeighbors(index: number, rows: number, cols: number) {
  const neighbors = [];
  const r = Math.floor(index / cols);
  const c = index % cols;

  if (c > 0) neighbors.push(index - 1); // Left
  if (c < cols - 1) neighbors.push(index + 1); // Right
  if (r > 0) neighbors.push(index - cols); // Up
  if (r < rows - 1) neighbors.push(index + cols); // Down

  return neighbors;
}

function createShuffledTiles(rows: number, cols: number) {
  const solved = generateSolvedState(rows, cols);
  const newTiles = [...solved];
  let emptyIdx = newTiles.length - 1; // 0 is at end
  let previousIdx = -1;

  // Simulate random valid moves
  const shuffleMoves = rows * cols * 5; // More moves for larger boards
  for (let i = 0; i < shuffleMoves; i++) {
      const neighbors = getNeighbors(emptyIdx, rows, cols);
      const candidates = neighbors.filter(n => n !== previousIdx);
      if (candidates.length === 0) continue;

      const randomNeighbor = candidates[Math.floor(Math.random() * candidates.length)];

      [newTiles[emptyIdx], newTiles[randomNeighbor]] = [newTiles[randomNeighbor], newTiles[emptyIdx]];
      previousIdx = emptyIdx;
      emptyIdx = randomNeighbor;
  }

  return newTiles;
}

export default function PuzzleSlideGame({ onComplete }: PuzzleSlideGameProps) {
  const [boardSize, setBoardSize] = useState<{rows: number, cols: number} | null>(null);
  const [tiles, setTiles] = useState<number[]>([]);
  const [isSolved, setIsSolved] = useState(false);
  const [moves, setMoves] = useState(0);

  // Initialize game when size changes
  useEffect(() => {
    if (boardSize) {
      setTiles(createShuffledTiles(boardSize.rows, boardSize.cols));
      setIsSolved(false);
      setMoves(0);
    }
  }, [boardSize]);

  const shuffleBoard = (rows: number, cols: number) => {
    setTiles(createShuffledTiles(rows, cols));
    setIsSolved(false);
    setMoves(0);
  };




  const handleTileClick = (index: number) => {
    if (isSolved || !boardSize) return;
    const { rows, cols } = boardSize;

    const emptyIdx = tiles.indexOf(0);
    const neighbors = getNeighbors(emptyIdx, rows, cols);

    if (neighbors.includes(index)) {
      const newTiles = [...tiles];
      [newTiles[emptyIdx], newTiles[index]] = [newTiles[index], newTiles[emptyIdx]];
      setTiles(newTiles);
      setMoves(m => m + 1);
      
      checkWin(newTiles, rows, cols);
    }
  };

  const checkWin = (currentTiles: number[], rows: number, cols: number) => {
      const solved = generateSolvedState(rows, cols);
      const isWin = currentTiles.every((val, index) => val === solved[index]);
      
      if (isWin) {
          setIsSolved(true);
          const score = Math.max(100 - moves, 20); 
          setTimeout(() => onComplete(score), 1500);
      }
  };

  if (!boardSize) {
      return (
        <div className="flex flex-col items-center justify-center h-full p-4 text-foreground space-y-8">
            <h2 className="text-3xl font-bold flex items-center gap-2">
                <Grid className="text-cyan-400" size={40} /> בחר גודל לוח
            </h2>
            <div className="flex flex-col gap-4 w-full max-w-xs">
                <button 
                    onClick={() => setBoardSize({ rows: 3, cols: 3 })}
                    dir="ltr"
                    className="bg-muted hover:bg-cyan-600 p-6 rounded-2xl text-2xl font-bold transition-all hover:scale-105 shadow-lg border-2 border-border hover:border-cyan-400"
                >
                    3 X 3
                </button>
                <button 
                    onClick={() => setBoardSize({ rows: 4, cols: 4 })}
                    dir="ltr"
                    className="bg-muted hover:bg-purple-600 p-6 rounded-2xl text-2xl font-bold transition-all hover:scale-105 shadow-lg border-2 border-border hover:border-purple-400"
                >
                    4 X 4
                </button>
                <button 
                    onClick={() => setBoardSize({ rows: 4, cols: 3 })} 
                    dir="ltr"
                    className="bg-muted hover:bg-pink-600 p-6 rounded-2xl text-2xl font-bold transition-all hover:scale-105 shadow-lg border-2 border-border hover:border-pink-400"
                >
                    3 X 4
                </button>
            </div>
        </div>
      );
  }

  return (
    <div className="flex flex-col items-center justify-center h-full p-4 text-foreground">
        <div className="flex items-center justify-between w-full max-w-md px-4 mb-4">
             <button onClick={() => setBoardSize(null)} className="text-muted-foreground hover:text-foreground text-sm">חזרה</button>
             <h2 className="text-2xl font-bold flex items-center gap-2">
                <Grid className="text-cyan-400" /> פאזל הזזה
            </h2>
             <span className="text-muted-foreground font-mono">{moves} מהלכים</span>
        </div>

        <div 
            className="grid gap-1 bg-muted p-2 rounded-xl touch-none"
            style={{ 
                gridTemplateColumns: `repeat(${boardSize.cols}, minmax(0, 1fr))`,
                maxWidth: '100%',
                maxHeight: '70vh',
                aspectRatio: `${boardSize.cols}/${boardSize.rows}`
            }}
        >
            {tiles.map((tile, i) => (
                <button
                    key={i}
                    onClick={() => handleTileClick(i)}
                    disabled={tile === 0 || isSolved}
                    className={`flex items-center justify-center text-2xl sm:text-3xl font-bold rounded-lg transition-all select-none ${
                        tile === 0 
                         ? 'invisible' 
                         : isSolved 
                            ? 'bg-green-500 text-white' 
                            : 'bg-muted hover:bg-accent text-foreground shadow-md active:scale-95'
                    }`}
                    style={{
                        width: 'clamp(3rem, 15vw, 6rem)', 
                        height: 'clamp(3rem, 15vw, 6rem)' 
                    }}
                >
                    {tile}
                </button>
            ))}
        </div>

        {isSolved && (
            <div className="mt-8 text-2xl font-bold text-green-400 animate-bounce">
                כל הכבוד!
            </div>
        )}
        
        <button 
            onClick={() => shuffleBoard(boardSize.rows, boardSize.cols)} 
            className="mt-8 text-sm text-muted-foreground underline hover:text-foreground"
        >
            ערבב מחדש
        </button>
    </div>
  );
}
